// 📁 src/pages/Swords.js 
import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/seo";
import cardData from "../components/card-data";

/* =========================================================
   THEME TOKENS (match MajorArcana.js / Wands.js / Cups.js)
========================================================= */
const WRAP = "px-[1vw]";
const MAXW = "max-w-10xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const BORDER_SOFT = "border-[#005C83]/12";
const PANEL_BASE =
  "relative overflow-hidden rounded-md " +
  BORDER_SOFT +
  " shadow-[0_2px_4px_rgba(0,0,0,0.35)]";

/* Canvas background */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;

/* Silk panel */
const SILK_TURQ =
  "bg-[linear-gradient(135deg,rgba(12,63,107,0.4),rgba(20,33,61,0.88))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(80%_60%_at_80%_0%,rgba(59,198,214,0.25),transparent_100%)] before:content-['']";

/* =========================================================
   SEO (About.js style via <Seo />)
========================================================= */
const siteUrl = "https://tarotfate.com";
const pageUrl = `${siteUrl}/tarot/swords-tarot-suit-cards-meaning`; // matches App.js
const ogImage = `${siteUrl}/icons/og-cover.jpg`; // ✅ use site-wide OG cover like About.js
const logoWebp = `${siteUrl}/icons/logo.webp`;

const metaDescription =
  "Study the Swords suit in depth. Explore images and meanings for all fourteen cards that speak to thought, truth, analysis, and clear communication with practical guidance for readings.";

const megaKeywords = `
  tarot, swords suit, swords tarot meanings, air element tarot, intellect in tarot, truth and clarity,
  upright meanings, reversed meanings, learn tarot, tarot study, tarot interpretation,
  ace of swords, two of swords, three of swords, four of swords, five of swords, six of swords,
  seven of swords, eight of swords, nine of swords, ten of swords, page of swords,
  knight of swords, queen of swords, king of swords,
  communication and ethics, conflict resolution, boundaries and decisions, beginner tarot, advanced tarot,
  tarot in English, tarot en Español, tarot em Português, tarot en Français,
  tarot Deutsch, tarocchi Italiano, tarot Türkçe, tarot 日本語, tarot 한국어
`.replace(/\s+/g, " ").trim();

/* =========================================================
   Data: exact 14 slugs for Swords
========================================================= */
const swordsSlugs = [
  "ace-of-swords-tarot-card-reading-meaning",
  "two-of-swords-tarot-card-reading-meaning",
  "three-of-swords-tarot-card-reading-meaning",
  "four-of-swords-tarot-card-reading-meaning",
  "five-of-swords-tarot-card-reading-meaning",
  "six-of-swords-tarot-card-reading-meaning",
  "seven-of-swords-tarot-card-reading-meaning",
  "eight-of-swords-tarot-card-reading-meaning",
  "nine-of-swords-tarot-card-reading-meaning",
  "ten-of-swords-tarot-card-reading-meaning",
  "page-of-swords-tarot-card-reading-meaning",
  "knight-of-swords-tarot-card-reading-meaning",
  "queen-of-swords-tarot-card-reading-meaning",
  "king-of-swords-tarot-card-reading-meaning",
];

const swordsCards = Object.values(cardData).filter((c) => swordsSlugs.includes(c.slug));

/* =========================================================
   JSON-LD (CollectionPage + ItemList + FAQPage)
========================================================= */
function schemaGraphFor(items, faqEntities) {
  const itemListElement = items.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    url: `${siteUrl}/tarot-card/${c.slug}`,
  }));

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: {
          "@type": "ImageObject",
          url: logoWebp, // ✅ logo.webp
          width: 512,
          height: 512,
        },
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: "en",
      },
      {
        "@type": "CollectionPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "Swords Tarot Cards | Meanings and Symbols | TarotFate",
        description: metaDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@id": `${pageUrl}#image` },
        breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
        inLanguage: "en",
      },
      {
        "@type": "ImageObject",
        "@id": `${pageUrl}#image`,
        url: ogImage, // ✅ use global OG cover
        width: 1200,
        height: 630,
        caption: "TarotFate cover",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
          { "@type": "ListItem", position: 2, name: "Tarot Cards", item: `${siteUrl}/tarot-cards` },
          { "@type": "ListItem", position: 3, name: "Swords", item: pageUrl },
        ],
      },
      {
        "@type": "ItemList",
        "@id": `${pageUrl}#swords`,
        name: "Swords",
        itemListElement,
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        mainEntity: faqEntities,
      },
    ],
  };
}

/* =========================================================
   Copy blocks with no dashes in visible text
========================================================= */
const suitIntro =
  "The Swords suit focuses on thought, truth, analysis, and communication. Aligned with air, it explores beliefs, choices, and the role of the mind in progress. From the Ace’s insight to the King’s discernment, Swords trace the path from tension to clarity. In readings, they highlight honest dialogue, clear thinking, and decisions that balance reason and compassion. Swords can signal conflict, anxiety, or mental fatigue, but also offer tools for resolution. The suit encourages gathering facts, testing assumptions, and making decisions with care. Swords teach that clarity is a practice, and precise language and ethical intent make the mind a guiding ally.";

const heroExplainer = "";

/* =========================================================
   Component (static; no scroll-reveal)
========================================================= */
export default function Swords() {
  const faq = [
    {
      q: "What do Swords represent in tarot?",
      a: "Swords represent the element of air. They describe intellect, truth, analysis, communication, and the setting of healthy boundaries. In a reading they highlight thinking patterns, conversations, and decisions.",
    },
    {
      q: "How many Swords cards are in a deck?",
      a: "There are fourteen Swords cards from Ace through King. The suit includes ten numbered cards and four court cards called Page, Knight, Queen, and King.",
    },
    {
      q: "Are Swords always negative?",
      a: "No. Swords can show challenge and conflict, yet they also bring clarity, honesty, and the courage to name what is true. They support decisions that create fairness and integrity.",
    },
    {
      q: "What does it mean when many Swords appear together?",
      a: "Many Swords suggest a time that centers on thought, dialogue, and important choices. The spread may ask for precise language, fact finding, and a plan that aligns with your values.",
    },
    {
      q: "How do I read difficult Swords like Three of Swords or Ten of Swords?",
      a: "Begin by naming the core truth, then look for sources of support and the next small step. Many Swords cards include a message about recovery, release, and a return to clarity.",
    },
    {
      q: "Should I use reversed meanings for Swords?",
      a: "Use reversals if they add helpful nuance. If you prefer, read upright meanings and rely on position and question for context. Choose one approach and apply it with consistency.",
    },
    {
      q: "Are these meanings a substitute for professional advice?",
      a: "No. Tarot supports reflection and growth. It does not replace medical, legal, or financial guidance. Please consult a qualified professional when needed.",
    },
  ];

  const faqEntities = faq.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  }));

  const schemaGraph = schemaGraphFor(swordsCards, faqEntities);

  return (
    <div className={`${FONT} text-white min-h-screen relative overflow-hidden`} style={{ background: CANVAS_BG }}>
      {/* Centralized SEO (consistent with About.js) */}
      <Seo
        title="Swords Tarot Cards | Meanings and Symbols | TarotFate"
        description={metaDescription}
        url={pageUrl}
        image={ogImage}
        schema={schemaGraph}
        keywords={megaKeywords}
        includeMega={true}
      />

      {/* Ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* HERO */}
      <section className={`pt-6 ${WRAP} ${MAXW}`}>
        <h1 className="text-xl sm:text-2xl tracking-wide text-center font-bold">Swords Tarot Cards</h1>
        <div className="flex items-center justify-center mb-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
          <span className="px-4 text-[#f3eaff]/100">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
        </div>
        <p className={`${TEXT} text-base mt-2`}>{suitIntro}</p>
        <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{heroExplainer}</p>
      </section>

      {/* SINGLE SUIT PANEL */}
      <section className={`mt-6 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-3 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ Swords</h2>
            <p className={`${TEXT} text-sm leading-relaxed mt-2`}>
              The cards below present the full suit. Open any card to study upright and reversed meanings,
              symbolism, and guidance for communication, planning, and personal development.
            </p>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {swordsCards.map((card) => (
                <Link
                  key={card.slug}
                  to={`/tarot-card/${card.slug}`}
                  className="group rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/25 hover:-translate-y-1 transition-transform duration-100"
                >
                  <div className="p-2 flex flex-col items-center gap-2">
                    {/* Card image on top, fully visible (no crop) */}
                    <div className="w-full aspect-[2/3] rounded-lg bg-white/5 border border-white/10 overflow-hidden">
                      <img
                        src={card.image || `/card/${card.slug}.webp`}
                        alt={card.name}
                        className="w-full h-full object-contain select-none pointer-events-none"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>

                    {/* Title + subtitle below the card */}
                    <div className="text-center">
                      <h3 className="text-sm font-semibold text-white/95">{card.name}</h3>
                      {card.subtitle ? (
                        <p className="text-xs italic text-white/75">{card.subtitle}</p>
                      ) : null}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

    {/* FAQ (always open, colored Q/A) */}
<section id="faq" className={`mt-12 mb-16 ${WRAP} ${MAXW}`}>
  <div className="text-center">
    <h2 className="text-base sm:text-lg font-medium">Frequently Asked Questions</h2>
  </div>

  <div className="mt-6 rounded-sm border border-white/35 bg-white/5">
    <div className="divide-y divide-white/20">
      {faq.map(({ q, a }, i) => (
        <div key={i} className="px-4 md:px-6 py-4">
          {/* Question pill (on-theme) */}
          <p className="font-semibold text-sm sm:text-base leading-relaxed px-3 py-2 rounded-md text-white bg-white/10">
            {q}
          </p>

          {/* Answer */}
          <p className="mt-2 text-white/90 text-sm sm:text-base leading-relaxed px-3">
            {a}
          </p>
        </div>
      ))}
    </div>
  </div>

  {/* Explore other suits */}
  <div className="text-center mt-10">
    <div className="relative inline-flex flex-wrap justify-center gap-2 sm:gap-3">
      <Link
        to="/tarot/wands-tarot-suit-cards-meaning"
        className="px-3 py-1.5 rounded-full bg-white/15 border border-white/20 text-white/95 text-sm font-medium hover:bg-white/25 hover:border-white/30 transition"
      >
        ✦ Wands
      </Link>
      <Link
        to="/tarot/cups-tarot-suit-cards-meaning"
        className="px-3 py-1.5 rounded-full bg-white/15 border border-white/20 text-white/95 text-sm font-medium hover:bg-white/25 hover:border-white/30 transition"
      >
        ✦ Cups
      </Link>
      <Link
        to="/tarot/pentacles-tarot-suit-cards-meaning"
        className="px-3 py-1.5 rounded-full bg-white/15 border border-white/20 text-white/95 text-sm font-medium hover:bg-white/25 hover:border-white/30 transition"
      >
        ✦ Pentacles
      </Link>
      <Link
        to="/tarot/major-arcana-tarot-suit-cards-meaning"
        className="px-3 py-1.5 rounded-full bg-white/15 border border-white/20 text-white/95 text-sm font-medium hover:bg-white/25 hover:border-white/30 transition"
      >
        ✦ Major Arcana
      </Link>
      <Link
        to="/tarot-cards"
        className="px-3 py-1.5 rounded-full bg-white/15 border border-white/20 text-white/95 text-sm font-medium hover:bg-white/25 hover:border-white/30 transition"
      >
        ✦ All Cards
      </Link>
    </div>
  </div>
</section>

    </div>
  );
}
